/**
 * WandTypeRef helpers: union case discrimination, identity key, rendering,
 * structural compatibility. Mirrors backend/services/typeref_io.py.
 *
 * The Thrift TS binding emits WandTypeRef as a class with optional fields,
 * exactly one of which is set per the union invariant. Direct field
 * presence checks work; consumers should go through caseOf() so a future
 * case is a single grep target.
 */

import {
  WandMapTypeRef,
  WandNamedTypeRef,
  WandPrimitive,
  WandTypeRef,
} from '@helinks/wand-api-thrift/wand_types';

export const WAND_BUILTIN_NAMESPACE = 'helinks.wand';

export type UnionCase =
  | 'primitive'
  | 'structRef'
  | 'enumRef'
  | 'listElement'
  | 'setElement'
  | 'map';

const CASE_FIELDS: UnionCase[] = [
  'primitive', 'structRef', 'enumRef', 'listElement', 'setElement', 'map',
];

export function caseOf(t: WandTypeRef): UnionCase {
  const set = CASE_FIELDS.filter((c) => (t as any)[c] !== undefined && (t as any)[c] !== null);
  if (set.length !== 1) {
    throw new Error(`WandTypeRef must have exactly one case set; got ${JSON.stringify(set)}`);
  }
  return set[0];
}

export function typeRefToString(t: WandTypeRef): string {
  const c = caseOf(t);
  switch (c) {
    case 'primitive': return WandPrimitive[t.primitive!].toLowerCase();
    case 'structRef': return t.structRef!.name;
    case 'enumRef': return t.enumRef!.name;
    case 'listElement': return `list<${typeRefToString(t.listElement!)}>`;
    case 'setElement': return `set<${typeRefToString(t.setElement!)}>`;
    case 'map': return `map<${typeRefToString(t.map!.keyType)},${typeRefToString(t.map!.valueType)}>`;
    default: { const _exhaustive: never = c; throw new Error(`unhandled case ${_exhaustive}`); }
  }
}

export function typeRefToDict(t: WandTypeRef): Record<string, unknown> {
  const c = caseOf(t);
  switch (c) {
    case 'primitive': return { primitive: WandPrimitive[t.primitive!] };
    case 'structRef': return { structRef: { namespace: t.structRef!.namespace, name: t.structRef!.name } };
    case 'enumRef': return { enumRef: { namespace: t.enumRef!.namespace, name: t.enumRef!.name } };
    case 'listElement': return { listElement: typeRefToDict(t.listElement!) };
    case 'setElement': return { setElement: typeRefToDict(t.setElement!) };
    case 'map': return { map: { keyType: typeRefToDict(t.map!.keyType), valueType: typeRefToDict(t.map!.valueType) } };
    default: { const _exhaustive: never = c; throw new Error(`unhandled case ${_exhaustive}`); }
  }
}

export function typeRefStableKey(t: WandTypeRef): string {
  return canonicalJsonStringify(typeRefToDict(t));
}

export function typeRefCompatible(a: WandTypeRef, b: WandTypeRef): boolean {
  const ca = caseOf(a);
  const cb = caseOf(b);
  if (ca !== cb) return false;
  switch (ca) {
    case 'primitive': return a.primitive === b.primitive;
    case 'structRef':
      return a.structRef!.namespace === b.structRef!.namespace
          && a.structRef!.name === b.structRef!.name;
    case 'enumRef':
      return a.enumRef!.namespace === b.enumRef!.namespace
          && a.enumRef!.name === b.enumRef!.name;
    case 'listElement': return typeRefCompatible(a.listElement!, b.listElement!);
    case 'setElement': return typeRefCompatible(a.setElement!, b.setElement!);
    case 'map':
      return typeRefCompatible(a.map!.keyType, b.map!.keyType)
          && typeRefCompatible(a.map!.valueType, b.map!.valueType);
    default: { const _exhaustive: never = ca; throw new Error(`unhandled case ${_exhaustive}`); }
  }
}

function canonicalJsonStringify(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(canonicalJsonStringify).join(',')}]`;
  const keys = Object.keys(v as Record<string, unknown>).sort();
  const body = keys.map((k) => `${JSON.stringify(k)}:${canonicalJsonStringify((v as Record<string, unknown>)[k])}`).join(',');
  return `{${body}}`;
}

// Sentinel WandTypeRef constants for reserved __code__ / __runtime_instance__ edges.
export const SENTINEL_TYPE_CODE_REF = new WandTypeRef({ primitive: WandPrimitive.STRING });
export const SENTINEL_TYPE_PYTHON_CODE_REF = new WandTypeRef({ primitive: WandPrimitive.PYTHON_CODE });
export const SENTINEL_TYPE_RUNTIME_INSTANCE_REF = new WandTypeRef({
  structRef: new WandNamedTypeRef({
    namespace: WAND_BUILTIN_NAMESPACE,
    name: 'WandRuntimeInstance',
  }),
});

const PRIMITIVE_BY_NAME: Record<string, WandPrimitive> = {
  bool: WandPrimitive.BOOL,
  byte: WandPrimitive.BYTE,
  i8: WandPrimitive.I8,
  i16: WandPrimitive.I16,
  i32: WandPrimitive.I32,
  i64: WandPrimitive.I64,
  double: WandPrimitive.DOUBLE,
  string: WandPrimitive.STRING,
  binary: WandPrimitive.BINARY,
  python_code: WandPrimitive.PYTHON_CODE,
};

/** Build a WandTypeRef from a picker label. Primitives are looked up by
 *  lowercase name; struct/enum names need a namespace resolver supplied
 *  by the caller (typically `namespaceOfNamedType` from the schema store). */
export function typeRefFromString(
  s: string,
  resolveNamespace: (name: string) => string | null,
): WandTypeRef {
  if (s in PRIMITIVE_BY_NAME) {
    return new WandTypeRef({ primitive: PRIMITIVE_BY_NAME[s] });
  }
  const ns = resolveNamespace(s);
  if (ns === null) {
    throw new Error(`unknown type: ${s}`);
  }
  return new WandTypeRef({ structRef: new WandNamedTypeRef({ namespace: ns, name: s }) });
}
